/**
 * Featured product image carousel — horizontal slide, arrow navigation only.
 */
function initFeaturedProductCarousels() {
	document.querySelectorAll( '[data-fp-carousel]' ).forEach( ( root ) => {
		const viewport = root.querySelector( '.fp-carousel-viewport' );
		const track = root.querySelector( '.fp-carousel-track' );
		const slides = root.querySelectorAll( '.fp-carousel-slide' );
		const prev = root.querySelector( '.fp-carousel-arrow--prev' );
		const next = root.querySelector( '.fp-carousel-arrow--next' );

		if ( ! viewport || ! track || slides.length < 2 || ! prev || ! next ) {
			return;
		}

		let index = 0;
		let frameId = 0;

		const syncCarousel = () => {
			const slideWidth = Math.round( viewport.getBoundingClientRect().width );

			if ( slideWidth < 1 ) {
				return false;
			}

			viewport.style.setProperty( '--fp-slide-width', `${ slideWidth }px` );

			slides.forEach( ( slide ) => {
				slide.style.flex = `0 0 ${ slideWidth }px`;
				slide.style.width = `${ slideWidth }px`;
				slide.style.maxWidth = `${ slideWidth }px`;
			} );

			track.style.transform = `translate3d(-${ slideWidth * index }px, 0, 0)`;

			slides.forEach( ( slide, slideIndex ) => {
				const isActive = slideIndex === index;
				slide.classList.toggle( 'is-active', isActive );
				slide.setAttribute( 'aria-hidden', isActive ? 'false' : 'true' );
			} );

			prev.disabled = index <= 0;
			next.disabled = index >= slides.length - 1;
			prev.setAttribute( 'aria-disabled', prev.disabled ? 'true' : 'false' );
			next.setAttribute( 'aria-disabled', next.disabled ? 'true' : 'false' );

			return true;
		};

		const scheduleSync = () => {
			if ( frameId ) {
				cancelAnimationFrame( frameId );
			}

			frameId = requestAnimationFrame( () => {
				frameId = 0;

				if ( ! syncCarousel() ) {
					scheduleSync();
				}
			} );
		};

		const goTo = ( nextIndex, button ) => {
			index = nextIndex;
			scheduleSync();

			if ( button && typeof button.blur === 'function' ) {
				button.blur();
			}
		};

		prev.addEventListener( 'click', ( event ) => {
			event.preventDefault();
			event.stopPropagation();

			if ( index > 0 ) {
				goTo( index - 1, event.currentTarget );
			}
		} );

		next.addEventListener( 'click', ( event ) => {
			event.preventDefault();
			event.stopPropagation();

			if ( index < slides.length - 1 ) {
				goTo( index + 1, event.currentTarget );
			}
		} );

		scheduleSync();

		if ( typeof ResizeObserver !== 'undefined' ) {
			const observer = new ResizeObserver( () => {
				scheduleSync();
			} );
			observer.observe( viewport );
		} else {
			window.addEventListener( 'resize', scheduleSync );
		}

		window.addEventListener( 'load', scheduleSync, { once: true } );
	} );
}

function bootFeaturedProductCarousels() {
	initFeaturedProductCarousels();
	initFeaturedProductFeatureClamp();
}

function parsePx(value) {
	const n = Number.parseFloat(value);
	return Number.isFinite(n) ? n : 0;
}

function getLineHeightPx(el) {
	const styles = window.getComputedStyle(el);
	const lineHeight = styles.lineHeight;
	const fontSize = parsePx(styles.fontSize) || 16;

	if (!lineHeight || lineHeight === 'normal') {
		// Match our theme defaults used in the block (typically 1.5).
		return fontSize * 1.5;
	}

	return parsePx(lineHeight) || fontSize * 1.5;
}

function ensureOriginalText(li) {
	if (!li || li.nodeType !== 1) return;
	if (li.dataset.fpOriginalText) return;
	li.dataset.fpOriginalText = li.textContent || '';
}

function restoreOriginalText(ul) {
	ul.querySelectorAll('li').forEach((li) => {
		ensureOriginalText(li);
		li.textContent = li.dataset.fpOriginalText;
	});
}

function trimTextToFit(li, ul, targetHeight) {
	const original = li.dataset.fpOriginalText || li.textContent || '';
	const text = original.trim();

	if (!text) return;

	let lo = 0;
	let hi = text.length;
	let best = '';

	// Binary search for the longest substring that fits.
	while (lo <= hi) {
		const mid = Math.floor((lo + hi) / 2);
		const candidate = text.slice(0, mid).trimEnd();
		li.textContent = candidate ? `${candidate}…` : '';

		if (ul.scrollHeight <= targetHeight) {
			best = li.textContent;
			lo = mid + 1;
		} else {
			hi = mid - 1;
		}
	}

	li.textContent = best || '…';
}

function clampFeatureList(ul, maxLines = 4) {
	if (!ul) return;

	// Reset before measuring/clamping (important on resize).
	restoreOriginalText(ul);

	const styles = window.getComputedStyle(ul);
	const paddingTop = parsePx(styles.paddingTop);
	const paddingBottom = parsePx(styles.paddingBottom);
	const lineHeightPx = getLineHeightPx(ul);

	const targetHeight = paddingTop + paddingBottom + lineHeightPx * maxLines;

	ul.style.maxHeight = `${targetHeight}px`;
	ul.style.overflow = 'hidden';

	// If it already fits, nothing to do.
	if (ul.scrollHeight <= targetHeight) return;

	// Remove whole list items from the bottom until close.
	while (ul.scrollHeight > targetHeight && ul.children.length > 1) {
		ul.removeChild(ul.lastElementChild);
	}

	// If the last item still overflows, trim it with ellipsis.
	if (ul.scrollHeight > targetHeight && ul.lastElementChild) {
		const lastLi = ul.lastElementChild;
		ensureOriginalText(lastLi);
		trimTextToFit(lastLi, ul, targetHeight);
	}
}

function initFeaturedProductFeatureClamp() {
	const lists = Array.from(
		document.querySelectorAll(
			'.geocare-featured-products .product-content .product-features'
		)
	);

	if (!lists.length) return;

	lists.forEach((ul) => {
		ul.querySelectorAll('li').forEach(ensureOriginalText);
		clampFeatureList(ul, 4);
	});

	// Re-clamp on resize (layout changes can affect line wrapping).
	let raf = 0;
	const onResize = () => {
		if (raf) cancelAnimationFrame(raf);
		raf = requestAnimationFrame(() => {
			raf = 0;
			lists.forEach((ul) => clampFeatureList(ul, 4));
		});
	};

	if (typeof ResizeObserver !== 'undefined') {
		const observer = new ResizeObserver(onResize);
		lists.forEach((ul) => observer.observe(ul));
	} else {
		window.addEventListener('resize', onResize);
	}
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', bootFeaturedProductCarousels );
} else {
	bootFeaturedProductCarousels();
}
